import { useReducer, useState } from 'react'
import { Text, TextInput, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { AppScreenTopBar } from '@/src/components/AppScreenTopBar'
import { Button } from '@/src/components/ui/Button'
import { KeyboardAwareScrollLayout } from '@/src/components/ui/KeyboardAwareScrollLayout'
import { useRequiredAppSessionUser } from '@/src/hooks/useRequiredAppSessionUser'
import { useToast } from '@/src/hooks/useToast'
import { colors } from '@/src/theme'
import { body3, body4, headline2, label2 } from '@/src/theme/typography'
import { consumePendingCrashReport } from '@/src/utils/pending-crash-report'
import { submitGeneralIssueReport } from '@/src/utils/sentry'
import { useTranslation } from 'react-i18next'

const MIN_MESSAGE_LENGTH = 10

type ReportIssueFormState = {
  email: string
  isSubmitting: boolean
  message: string
  name: string
  validationError: string | null
}

type ReportIssueFormAction =
  | { type: 'setEmail'; value: string }
  | { type: 'setIsSubmitting'; value: boolean }
  | { type: 'setMessage'; value: string }
  | { type: 'setName'; value: string }
  | { type: 'setValidationError'; value: string | null }

function reportIssueFormReducer(
  state: ReportIssueFormState,
  action: ReportIssueFormAction,
): ReportIssueFormState {
  switch (action.type) {
    case 'setEmail':
      return { ...state, email: action.value }
    case 'setIsSubmitting':
      return { ...state, isSubmitting: action.value }
    case 'setMessage':
      return { ...state, message: action.value }
    case 'setName':
      return { ...state, name: action.value }
    case 'setValidationError':
      return { ...state, validationError: action.value }
  }
}

function normalizeParam(
  value: string | string[] | undefined,
): string | undefined {
  if (Array.isArray(value)) {
    return value[0]
  }
  return value
}

export default function ReportIssueScreen() {
  const { back, canGoBack, replace } = useRouter()
  const pathname = usePathname()
  const insets = useSafeAreaInsets()
  const { authUser, backendUser } = useRequiredAppSessionUser()
  const { t } = useTranslation(['profile', 'common'])
  const { showToast } = useToast()
  const { eventId: eventIdParam } = useLocalSearchParams<{
    eventId?: string | string[]
  }>()

  const associatedEventId = normalizeParam(eventIdParam)
  // Consume once on mount: the crash boundary stashes the error message and
  // component stack here so the feedback is useful even if the linked Sentry
  // event was dropped (e.g. deduped) and can't be found.
  const [crashReport] = useState(() => consumePendingCrashReport())
  const [formState, dispatchForm] = useReducer(reportIssueFormReducer, {
    email: authUser.email ?? '',
    isSubmitting: false,
    message: '',
    name: backendUser.firstName ?? '',
    validationError: null,
  })
  const { email, isSubmitting, message, name, validationError } = formState
  const scrollBottomInset = 32 + insets.bottom

  const handleClose = () => {
    if (canGoBack()) {
      back()
      return
    }

    replace('/settings')
  }

  const handleSubmit = async () => {
    const trimmedMessage = message.trim()
    if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
      dispatchForm({
        type: 'setValidationError',
        value: t('profile:report_issue_message_min_length', {
          count: MIN_MESSAGE_LENGTH,
        }),
      })
      return
    }

    dispatchForm({ type: 'setValidationError', value: null })
    dispatchForm({ type: 'setIsSubmitting', value: true })

    try {
      const didSubmit = await submitGeneralIssueReport({
        message: trimmedMessage,
        name,
        email,
        associatedEventId,
        errorMessage: crashReport?.message,
        componentStack: crashReport?.componentStack,
        auth0Subject: authUser.sub,
        revenueCatAppUser: backendUser.revenueCatId,
        route: pathname,
        routeParams: { eventId: associatedEventId },
      })

      if (!didSubmit) {
        dispatchForm({
          type: 'setValidationError',
          value: t('profile:report_issue_sentry_disabled'),
        })
        return
      }

      showToast({
        variant: 'success',
        title: t('profile:report_issue_success_title'),
        message: t('profile:report_issue_success_message'),
      })
      handleClose()
    } catch {
      dispatchForm({
        type: 'setValidationError',
        value: t('profile:report_issue_submit_failed'),
      })
    } finally {
      dispatchForm({ type: 'setIsSubmitting', value: false })
    }
  }

  return (
    <View className="flex-1 bg-background-primary">
      <Head>
        <title>{t('common:page_title_report_issue')}</title>
      </Head>
      <AppScreenTopBar />
      <KeyboardAwareScrollLayout
        className="flex-1"
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingTop: 20,
          paddingBottom: scrollBottomInset,
        }}
      >
        <View className="mb-6 rounded-lg bg-background-secondary px-5 py-5">
          <Text style={[headline2, { color: colors.textHeader }]}>
            {t('profile:report_issue_heading')}
          </Text>
          <Text className="mt-2" style={[body3, { color: colors.textBody }]}>
            {t('profile:report_issue_description')}
          </Text>
        </View>

        {associatedEventId != null ? (
          <View className="mb-5 rounded-lg border border-border bg-fill px-4 py-3">
            <Text style={[label2, { color: colors.textHeader }]}>
              {t('profile:report_issue_linked_event_title')}
            </Text>
            <Text className="mt-1" style={[body4, { color: colors.textBody }]}>
              {t('profile:report_issue_linked_event_message', {
                eventId: associatedEventId,
              })}
            </Text>
          </View>
        ) : null}

        <View className="gap-4">
          <View>
            <Text
              className="mb-2"
              style={[label2, { color: colors.textHeader }]}
            >
              {t('profile:report_issue_message_label')}
            </Text>
            <TextInput
              value={message}
              onChangeText={(value) => {
                dispatchForm({ type: 'setMessage', value })
                dispatchForm({ type: 'setValidationError', value: null })
              }}
              multiline
              textAlignVertical="top"
              editable={!isSubmitting}
              placeholder={t('profile:report_issue_message_placeholder')}
              placeholderTextColor={colors.muted}
              className="min-h-[176px] rounded-lg border border-border bg-background-secondary px-4 py-4 text-base text-ink"
            />
          </View>

          <View>
            <Text
              className="mb-2"
              style={[label2, { color: colors.textHeader }]}
            >
              {t('profile:report_issue_name_label')}
            </Text>
            <TextInput
              value={name}
              onChangeText={(value) => dispatchForm({ type: 'setName', value })}
              editable={!isSubmitting}
              placeholder={t('profile:report_issue_name_placeholder')}
              placeholderTextColor={colors.muted}
              autoCapitalize="words"
              className="h-[52px] rounded-lg border border-border bg-background-secondary px-4 text-base text-ink"
            />
          </View>

          <View>
            <Text
              className="mb-2"
              style={[label2, { color: colors.textHeader }]}
            >
              {t('profile:report_issue_email_label')}
            </Text>
            <TextInput
              value={email}
              onChangeText={(value) =>
                dispatchForm({ type: 'setEmail', value })
              }
              editable={!isSubmitting}
              placeholder={t('profile:report_issue_email_placeholder')}
              placeholderTextColor={colors.muted}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              className="h-[52px] rounded-lg border border-border bg-background-secondary px-4 text-base text-ink"
            />
          </View>
        </View>

        {validationError != null ? (
          <Text className="mt-4 text-center text-destructive">
            {validationError}
          </Text>
        ) : null}

        <View className="mt-6 gap-3">
          <Button
            label={t('profile:report_issue_submit')}
            onPress={() => void handleSubmit()}
            loading={isSubmitting}
            disabled={isSubmitting}
            variant="primary"
            size="md"
            containerClassName="w-full"
          />
          <Button
            label={t('common:cancel')}
            onPress={handleClose}
            disabled={isSubmitting}
            variant="neutral"
            size="md"
            containerClassName="w-full"
          />
        </View>
      </KeyboardAwareScrollLayout>
    </View>
  )
}
